import React, { useState } from 'react';
import { 
  Card, 
  Typography, 
  Steps, 
  Button, 
  Alert, 
  message, 
  Collapse, 
  Tag
} from 'antd';
import { 
  CreditCardOutlined, 
  CheckCircleOutlined, 
  InfoCircleOutlined, 
  ShoppingCartOutlined, 
  CodeOutlined
} from '@ant-design/icons';

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;
const { Step } = Steps;

const StripeIntegrationDemo: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [showDemo, setShowDemo] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Simule le processus de paiement réussi
  const handlePaymentSuccess = (paymentIntentId: string) => {
    message.success(`Paiement réussi ! ID: ${paymentIntentId}`);
    setPaymentSuccess(true);
  };

  // Simule une erreur de paiement
  const handlePaymentError = (error: any) => {
    message.error('Erreur de paiement: ' + (error.message || 'Une erreur est survenue'));
  };

  const steps = [
    {
      title: 'Configuration',
      content: (
        <>
          <Title level={4}>Configuration de Stripe</Title>
          <Paragraph>
            Suivez ces étapes pour configurer Stripe dans votre application React/TypeScript:
          </Paragraph>
          
          <Steps direction="vertical" size="small" current={-1}>
            <Step 
              title="Créer un compte Stripe" 
              description="Inscrivez-vous sur stripe.com et obtenez vos clés API (publique et secrète)." 
            />
            <Step 
              title="Installer les dépendances Stripe" 
              description="npm install @stripe/react-stripe-js @stripe/stripe-js --legacy-peer-deps" 
            />
            <Step 
              title="Configurer les variables d'environnement" 
              description="Stockez votre clé publique Stripe dans .env" 
            />
            <Step 
              title="Implémenter les composants Stripe" 
              description="Créez les composants StripeProvider et StripeCheckoutForm" 
            />
          </Steps>

          <Alert
            message="Sécurité"
            description="Ne stockez jamais votre clé secrète Stripe côté client. Utilisez-la uniquement dans votre backend."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        </>
      ),
    },
    {
      title: 'Composants',
      content: (
        <>
          <Title level={4}>Composants principaux de l'intégration</Title>
          
          <Collapse defaultActiveKey={['1']}>
            <Panel 
              header={<><CodeOutlined /> StripeProvider.tsx</>} 
              key="1"
            >
              <Paragraph>
                Ce composant encapsule l'intégration Stripe en utilisant le hook Elements.
              </Paragraph>
              <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.9em', overflowX: 'auto' }}>
                {`import { Elements } from '@stripe/react-stripe-js';\nimport { loadStripe } from '@stripe/stripe-js';\n\n// Ce composant charge Stripe et fournit le contexte à ses enfants`}
              </div>
            </Panel>
            
            <Panel 
              header={<><CodeOutlined /> StripeCheckoutForm.tsx</>} 
              key="2"
            >
              <Paragraph>
                Ce composant affiche le formulaire de carte de crédit et gère la soumission du paiement.
              </Paragraph>
              <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.9em', overflowX: 'auto' }}>
                {`import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';\n\n// Ce composant affiche le champ de carte et gère la soumission`}
              </div>
            </Panel>
            
            <Panel 
              header={<><CodeOutlined /> cartService.ts</>} 
              key="3"
            >
              <Paragraph>
                Le service de panier qui intègre les méthodes de paiement Stripe.
              </Paragraph>
              <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, fontFamily: 'monospace', fontSize: '0.9em', overflowX: 'auto' }}>
                {`// Méthode de traitement du paiement par carte\nasync processCardPayment(paymentIntentId: string): Promise<Order> {\n  // Implémentation de la finalisation du paiement\n}`}
              </div>
            </Panel>
          </Collapse>
        </>
      ),
    },
    {
      title: 'Démonstration',
      content: (
        <>
          <Title level={4}>Démonstration du formulaire de paiement</Title>
          
          <Paragraph>
            Voici comment le formulaire de paiement Stripe s'affiche dans votre application:
          </Paragraph>
          
          <Alert
            message="Mode de test"
            description={
              <div>
                <p>Utilisez ces numéros de carte pour tester:</p>
                <p><Tag color="green">Paiement réussi:</Tag> 4242 4242 4242 4242</p>
                <p><Tag color="red">Paiement échoué:</Tag> 4000 0000 0000 0002</p>
                <p>Pour les autres champs, vous pouvez utiliser n'importe quelle date future pour l'expiration, n'importe quel CVC à 3 chiffres et n'importe quel code postal à 5 chiffres.</p>
              </div>
            }
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
          
          {!showDemo ? (
            <Button 
              type="primary" 
              icon={<CreditCardOutlined />} 
              onClick={() => setShowDemo(true)}
            >
              Afficher le formulaire de démonstration
            </Button>
          ) : (
            <Card style={{ maxWidth: 500, margin: '0 auto' }}>
              {!paymentSuccess ? (              <div style={{ padding: 16, border: '1px solid #f0f0f0', borderRadius: 4 }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ padding: 10, background: '#f9f9f9', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                    Numéro de carte : <strong>4242 4242 4242 4242</strong>
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <span>Expiration:</span>
                  <input 
                    type="text" 
                    placeholder="MM/AA" 
                    style={{ width: 100, marginLeft: 8, padding: 8, border: '1px solid #d9d9d9' }} 
                  />
                  <span style={{ marginLeft: 16 }}>CVV:</span>
                  <input 
                    type="text" 
                    placeholder="123" 
                    style={{ width: 60, marginLeft: 8, padding: 8, border: '1px solid #d9d9d9' }} 
                  />
                </div>
                <Button 
                  type="primary" 
                  block 
                  icon={<CreditCardOutlined />} 
                  onClick={() => handlePaymentSuccess('pi_simulated_12345')}
                >
                  Payer 49.99 €
                </Button>
              </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                  <Title level={4} style={{ color: '#52c41a', marginTop: 16 }}>
                    Paiement réussi !
                  </Title>
                  <Button type="primary" onClick={() => {
                    setShowDemo(false);
                    setPaymentSuccess(false);
                  }}>
                    Réinitialiser la démonstration
                  </Button>
                </div>
              )}
            </Card>
          )}
        </>
      ),
    },
  ];

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  return (
    <Card title={<><CreditCardOutlined /> Intégration Stripe pour le paiement par carte</>}>
      <Steps current={current}>
        <Step key="config" title="Configuration" icon={<InfoCircleOutlined />} />
        <Step key="components" title="Composants" icon={<CodeOutlined />} />
        <Step key="demo" title="Démonstration" icon={<ShoppingCartOutlined />} />
      </Steps>
      
      <div style={{ marginTop: 24, padding: 24, backgroundColor: '#fafafa', minHeight: 400 }}>
        {steps[current].content}
      </div>
      
      <div style={{ marginTop: 24 }}>
        {current > 0 && (
          <Button style={{ margin: '0 8px' }} onClick={() => prev()}>
            Précédent
          </Button>
        )}
        {current < steps.length - 1 && (
          <Button type="primary" onClick={() => next()}>
            Suivant
          </Button>
        )}
      </div>
    </Card>
  );
};

export default StripeIntegrationDemo;
